/* site/cms/schema.js — THE CONTRACT of the website builder (2026-09-12).
 *
 * A CMS page is a list of SECTIONS; a section is a full-width band holding BLOCKS. Blocks are
 * plain JSON, rendered by the same components in the builder canvas and on the public site, so
 * what you drag is exactly what visitors get. Everything a block can be is declared here ONCE:
 *   · `BLOCKS[type]`   — label, icon, default props, and the settings FIELDS the inspector shows
 *   · `STYLE_FIELDS`   — the layout / typography / colour fields every block and section shares
 *   · `newBlock/newSection/newPage` — factories that stamp ids and defaults
 *   · `normalizePage`  — heals anything that comes back from the database
 *
 * Field kinds (the inspector renders them; renderers only read props/style):
 *   text · textarea · richtext · number · select · color · image · video · link · toggle
 *   · spacing (4 numbers t/r/b/l) · align · list (array of items with `item` sub-fields)
 */

let _seq = 0;
export function uid(prefix = "b") {
  _seq += 1;
  const r = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}${r}${_seq.toString(36)}`;
}

/** slugs are lowercase, dash-separated, no leading slash; "" = the site home is NOT allowed
 *  (the existing landing page owns "/") */
export function normalizeSlug(s) {
  return String(s || "")
    .trim().toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9/_-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

/* ── shared style fields ─────────────────────────────────────────────────── */
export const STYLE_FIELDS = [
  { key: "textAlign", label: "Alignment", kind: "align" },
  { key: "fontSize", label: "Font size (px)", kind: "number", min: 8, max: 160 },
  { key: "fontWeight", label: "Font weight", kind: "select", options: [["300", "Light"], ["400", "Regular"], ["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]] },
  { key: "fontFamily", label: "Font", kind: "select", options: [["", "Site default"], ["serif", "Serif"], ["mono", "Monospace"]] },
  { key: "lineHeight", label: "Line height", kind: "number", min: 0.8, max: 3, step: 0.05 },
  { key: "color", label: "Text colour", kind: "color" },
  { key: "background", label: "Background", kind: "color" },
  { key: "width", label: "Width (%)", kind: "number", min: 10, max: 100 },
  { key: "maxWidth", label: "Max width (px)", kind: "number", min: 200, max: 1920 },
  { key: "minHeight", label: "Min height (px)", kind: "number", min: 0, max: 1400 },
  { key: "padding", label: "Padding", kind: "spacing" },
  { key: "margin", label: "Margin", kind: "spacing" },
  { key: "borderRadius", label: "Corner radius (px)", kind: "number", min: 0, max: 200 },
  { key: "shadow", label: "Shadow", kind: "select", options: [["", "None"], ["sm", "Soft"], ["md", "Medium"], ["lg", "Deep"]] },
];

export const SECTION_STYLE_FIELDS = [
  { key: "background", label: "Background colour", kind: "color" },
  { key: "backgroundImage", label: "Background image", kind: "image" },
  { key: "color", label: "Text colour", kind: "color" },
  { key: "maxWidth", label: "Content width (px)", kind: "number", min: 320, max: 1920 },
  { key: "minHeight", label: "Min height (px)", kind: "number", min: 0, max: 1400 },
  { key: "padding", label: "Padding", kind: "spacing" },
  { key: "textAlign", label: "Alignment", kind: "align" },
  { key: "gap", label: "Block gap (px)", kind: "number", min: 0, max: 200 },
];

/* ── block registry ──────────────────────────────────────────────────────── */
export const BLOCKS = {
  heading: {
    label: "Heading", icon: "H", group: "Text",
    defaults: { text: "A headline that sells the room", level: 2 },
    fields: [
      { key: "text", label: "Text", kind: "text" },
      { key: "level", label: "Level", kind: "select", options: [["1", "H1"], ["2", "H2"], ["3", "H3"], ["4", "H4"]] },
    ],
    style: { textAlign: "left", fontWeight: "600" },
  },
  text: {
    label: "Text", icon: "¶", group: "Text",
    defaults: { html: "<p>Write your paragraph here. Double-click to edit the text in place.</p>" },
    fields: [{ key: "html", label: "Content", kind: "richtext" }],
    style: { textAlign: "left", fontSize: 16, lineHeight: 1.6 },
  },
  image: {
    label: "Image", icon: "▣", group: "Media",
    defaults: { src: "", alt: "", caption: "", fit: "cover", link: "" },
    fields: [
      { key: "src", label: "Image", kind: "image" },
      { key: "alt", label: "Alt text", kind: "text" },
      { key: "caption", label: "Caption", kind: "text" },
      { key: "fit", label: "Fit", kind: "select", options: [["cover", "Cover"], ["contain", "Contain"]] },
      { key: "link", label: "Link", kind: "link" },
    ],
    style: { width: 100, borderRadius: 0 },
  },
  video: {
    label: "Video", icon: "▶", group: "Media",
    defaults: { src: "", poster: "", autoplay: false, loop: true, muted: true, controls: true },
    fields: [
      { key: "src", label: "Video file or YouTube / Vimeo URL", kind: "video" },
      { key: "poster", label: "Poster image", kind: "image" },
      { key: "autoplay", label: "Autoplay", kind: "toggle" },
      { key: "loop", label: "Loop", kind: "toggle" },
      { key: "muted", label: "Muted", kind: "toggle" },
      { key: "controls", label: "Show controls", kind: "toggle" },
    ],
    style: { width: 100 },
  },
  button: {
    label: "Button", icon: "⬚", group: "Actions",
    defaults: { label: "Discover", href: "/products", variant: "solid", newTab: false },
    fields: [
      { key: "label", label: "Label", kind: "text" },
      { key: "href", label: "Link", kind: "link" },
      { key: "variant", label: "Style", kind: "select", options: [["solid", "Solid"], ["outline", "Outline"], ["text", "Text link"]] },
      { key: "newTab", label: "Open in new tab", kind: "toggle" },
    ],
    style: { textAlign: "left", fontSize: 14, fontWeight: "600", borderRadius: 999 },
  },
  gallery: {
    label: "Gallery", icon: "⊞", group: "Media",
    defaults: { columns: 3, gap: 12, items: [] },
    fields: [
      { key: "columns", label: "Columns", kind: "number", min: 1, max: 6 },
      { key: "gap", label: "Gap (px)", kind: "number", min: 0, max: 64 },
      { key: "items", label: "Images", kind: "list", item: [
        { key: "src", label: "Image", kind: "image" },
        { key: "alt", label: "Alt text", kind: "text" },
        { key: "caption", label: "Caption", kind: "text" },
      ] },
    ],
    style: { width: 100 },
  },
  cards: {
    label: "Cards", icon: "▤", group: "Layout",
    defaults: { columns: 3, gap: 24, items: [
      { title: "Horizon", text: "Kitchen system", image: "", href: "" },
      { title: "Vela", text: "Wardrobe system", image: "", href: "" },
      { title: "Atlas", text: "Living system", image: "", href: "" },
    ] },
    fields: [
      { key: "columns", label: "Columns", kind: "number", min: 1, max: 4 },
      { key: "gap", label: "Gap (px)", kind: "number", min: 0, max: 64 },
      { key: "items", label: "Cards", kind: "list", item: [
        { key: "image", label: "Image", kind: "image" },
        { key: "title", label: "Title", kind: "text" },
        { key: "text", label: "Text", kind: "textarea" },
        { key: "href", label: "Link", kind: "link" },
      ] },
    ],
    style: { width: 100, textAlign: "left" },
  },
  map: {
    label: "Map", icon: "◎", group: "Embeds",
    defaults: { query: "Lugano, Switzerland", zoom: 13, height: 360 },
    fields: [
      { key: "query", label: "Address or place", kind: "text" },
      { key: "zoom", label: "Zoom", kind: "number", min: 1, max: 20 },
      { key: "height", label: "Height (px)", kind: "number", min: 120, max: 900 },
    ],
    style: { width: 100, borderRadius: 8 },
  },
  spacer: {
    label: "Spacer", icon: "↕", group: "Layout",
    defaults: { height: 48 },
    fields: [{ key: "height", label: "Height (px)", kind: "number", min: 0, max: 600 }],
    style: {},
  },
  divider: {
    label: "Divider", icon: "—", group: "Layout",
    defaults: { thickness: 1 },
    fields: [{ key: "thickness", label: "Thickness (px)", kind: "number", min: 1, max: 12 }],
    style: { width: 100, color: "" },
  },
  columns: {
    label: "Columns", icon: "▥", group: "Layout",
    defaults: { columns: [[], []], gap: 24, ratio: "" },
    fields: [
      { key: "gap", label: "Gap (px)", kind: "number", min: 0, max: 96 },
      { key: "ratio", label: "Ratio", kind: "select", options: [["", "Equal"], ["1-2", "1 : 2"], ["2-1", "2 : 1"], ["1-1-1", "Thirds"]] },
    ],
    style: { width: 100 },
    container: true,          // props.columns = array of block arrays
  },
  hero: {
    label: "Hero", icon: "◧", group: "Layout",
    defaults: { image: "", title: "The 2026 Collection", subtitle: "Furniture systems for the whole home.", ctaLabel: "Discover", ctaHref: "/products", overlay: 0.35, height: 560 },
    fields: [
      { key: "image", label: "Background image", kind: "image" },
      { key: "title", label: "Title", kind: "text" },
      { key: "subtitle", label: "Subtitle", kind: "textarea" },
      { key: "ctaLabel", label: "Button label", kind: "text" },
      { key: "ctaHref", label: "Button link", kind: "link" },
      { key: "overlay", label: "Darken (0-1)", kind: "number", min: 0, max: 1, step: 0.05 },
      { key: "height", label: "Height (px)", kind: "number", min: 240, max: 1000 },
    ],
    style: { textAlign: "center", color: "#ffffff" },
  },
  embed: {
    label: "Embed (HTML)", icon: "</>", group: "Embeds",
    defaults: { html: "" },
    fields: [{ key: "html", label: "HTML", kind: "textarea" }],
    style: { width: 100 },
  },
};

export const BLOCK_GROUPS = ["Text", "Media", "Actions", "Layout", "Embeds"];

/* ── factories ───────────────────────────────────────────────────────────── */
export function newBlock(type, overrides = {}) {
  const def = BLOCKS[type];
  if (!def) throw new Error("unknown block type " + type);
  return {
    id: uid("b"), type,
    props: JSON.parse(JSON.stringify({ ...def.defaults, ...(overrides.props || {}) })),
    style: { ...(def.style || {}), ...(overrides.style || {}) },
  };
}

export function newSection(overrides = {}) {
  return {
    id: uid("s"), type: "section",
    name: overrides.name || "Section",
    style: { padding: [64, 24, 64, 24], maxWidth: 1200, gap: 24, ...(overrides.style || {}) },
    blocks: (overrides.blocks || []).map((b) => (b.id ? b : newBlock(b.type, b))),
  };
}

export function newPage(partial = {}) {
  const title = partial.title || "New page";
  return {
    id: partial.id || null,
    title,
    slug: normalizeSlug(partial.slug || title),
    status: "draft",
    draft: { sections: partial.sections || [newSection({ name: "Intro", blocks: [newBlock("heading", { props: { text: title, level: 1 } }), newBlock("text")] })] },
    published: null,
    seo: { title: "", description: "", image: "" },
    template: partial.template || "",
    order: partial.order != null ? partial.order : 0,
  };
}

/** heal a page document from the API (older shapes, missing ids) */
export function normalizePage(p) {
  if (!p) return null;
  const fixBlocks = (list) => (Array.isArray(list) ? list : []).filter(Boolean).map((b) => {
    const def = BLOCKS[b.type] || BLOCKS.text;
    const type = BLOCKS[b.type] ? b.type : "text";
    const out = { id: b.id || uid("b"), type, props: { ...def.defaults, ...(b.props || {}) }, style: { ...(def.style || {}), ...(b.style || {}) } };
    if (def.container) out.props.columns = (out.props.columns || [[], []]).map(fixBlocks);
    return out;
  });
  const fixSections = (list) => (Array.isArray(list) ? list : []).filter(Boolean).map((s) => ({
    id: s.id || uid("s"), type: "section", name: s.name || "Section",
    style: { padding: [64, 24, 64, 24], maxWidth: 1200, gap: 24, ...(s.style || {}) },
    blocks: fixBlocks(s.blocks),
  }));
  return {
    id: p.id || p._id || null,
    title: p.title || "Untitled",
    slug: normalizeSlug(p.slug || p.title || "page"),
    status: p.status === "published" ? "published" : "draft",
    draft: { sections: fixSections(p.draft && p.draft.sections) },
    published: p.published && Array.isArray(p.published.sections) ? { sections: fixSections(p.published.sections), publishedAt: p.published.publishedAt || null } : null,
    seo: { title: "", description: "", image: "", ...(p.seo || {}) },
    template: p.template || "",
    order: Number.isFinite(p.order) ? p.order : 0,
    createdAt: p.createdAt || null, updatedAt: p.updatedAt || null,
  };
}

/* ── style → CSS ─────────────────────────────────────────────────────────── */
const FONT = { "": "", serif: "Georgia, 'Times New Roman', serif", mono: "ui-monospace, SFMono-Regular, Menlo, monospace" };
const SHADOW = { "": "", sm: "0 4px 14px rgba(0,0,0,.08)", md: "0 10px 30px rgba(0,0,0,.14)", lg: "0 24px 60px rgba(0,0,0,.22)" };
const sp = (v) => (Array.isArray(v) ? v.map((n) => `${Number(n) || 0}px`).join(" ") : v == null ? undefined : `${v}px`);

/** the inline style a block/section renders with (shared by canvas + public renderer) */
export function styleToCss(style = {}, opts = {}) {
  const css = {};
  if (style.textAlign) css.textAlign = style.textAlign;
  if (style.fontSize) css.fontSize = `${style.fontSize}px`;
  if (style.fontWeight) css.fontWeight = style.fontWeight;
  if (style.fontFamily && FONT[style.fontFamily]) css.fontFamily = FONT[style.fontFamily];
  if (style.lineHeight) css.lineHeight = style.lineHeight;
  if (style.color) css.color = style.color;
  if (style.background) css.background = style.background;
  if (style.backgroundImage) { css.backgroundImage = `url(${style.backgroundImage})`; css.backgroundSize = "cover"; css.backgroundPosition = "center"; }
  if (style.width && !opts.noWidth) css.width = `${style.width}%`;
  if (style.maxWidth) css.maxWidth = `${style.maxWidth}px`;
  if (style.minHeight) css.minHeight = `${style.minHeight}px`;
  if (style.padding) css.padding = sp(style.padding);
  if (style.margin) css.margin = sp(style.margin);
  if (style.borderRadius != null && style.borderRadius !== "") css.borderRadius = `${style.borderRadius}px`;
  if (style.shadow && SHADOW[style.shadow]) css.boxShadow = SHADOW[style.shadow];
  if (style.gap != null && opts.gap) css.gap = `${style.gap}px`;
  return css;
}

/* ── tree helpers (pure, immutable) ──────────────────────────────────────── */
export function findBlock(sections, blockId) {
  for (const s of sections || []) {
    const hit = findIn(s.blocks, blockId, [s.id]);
    if (hit) return hit;
  }
  return null;
}
function findIn(blocks, id, path) {
  for (let i = 0; i < (blocks || []).length; i++) {
    const b = blocks[i];
    if (b.id === id) return { block: b, path, index: i };
    if (b.props && Array.isArray(b.props.columns)) {
      for (let c = 0; c < b.props.columns.length; c++) {
        const hit = findIn(b.props.columns[c], id, path.concat(b.id, c));
        if (hit) return hit;
      }
    }
  }
  return null;
}

/** map every block list in the tree through fn(list, ownerPath) — returns a new tree */
export function mapLists(sections, fn) {
  const mapBlocks = (blocks, path) => fn(blocks, path).map((b) => {
    if (b.props && Array.isArray(b.props.columns)) {
      return { ...b, props: { ...b.props, columns: b.props.columns.map((col, c) => mapBlocks(col, path.concat(b.id, c))) } };
    }
    return b;
  });
  return (sections || []).map((s) => ({ ...s, blocks: mapBlocks(s.blocks || [], [s.id]) }));
}

/** the block list at a path: [sectionId] or [sectionId, columnsBlockId, colIndex, ...] */
export function listAt(sections, path) {
  const s = (sections || []).find((x) => x.id === path[0]);
  if (!s) return null;
  let list = s.blocks;
  for (let i = 1; i < path.length; i += 2) {
    const b = (list || []).find((x) => x.id === path[i]);
    if (!b || !b.props || !Array.isArray(b.props.columns)) return null;
    list = b.props.columns[path[i + 1]];
  }
  return list || null;
}

export function samePath(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);
}
